import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { GlobalStore } from '../../../store';
import { joinQueue, leaveQueue, updatePersonalEntry } from '../../../actions/queueActions';
import { openSelectHelperModal } from '../../../actions/modalActions';
import User from '../../../models/User';
import QueueEntry from '../../../models/QueueEntry';
import { Lock } from '../../../viewcomponents/FontAwesome';
import EnterInformationForm from './EnterInformationForm';
import { HTTP_SERVER_URL } from '../../../configuration';

export default (props: any): JSX.Element => {

  const user = useSelector<GlobalStore, User | null>(store => store.user);

  const personalQueueEntry = useSelector<GlobalStore, QueueEntry | null>(
    store => store.queues
    .queueList
    .filter(q => q.name === props.queueName)[0]
    .queueEntries
    .filter(e => e.providerId === user?.providerId)[0] || null);

  const isLocked = useSelector<GlobalStore, boolean>(
    store => store.queues
    .queueList
    .filter(queue => queue.name === props.queueName)[0]
    .locked);

  const isTA = user !== null && (user.isTeacherIn(props.queueName) || user.isAssistantIn(props.queueName));

  const dispatch = useDispatch();

  const [location, setLocation] = useState(user?.location || personalQueueEntry?.location || '');
  const [comment, setComment] = useState(personalQueueEntry?.comment || '');
  const [help, setHelp] = useState(personalQueueEntry !== null ? personalQueueEntry.help : true);
  const [doubleClickProtection, setDoubleClickProtection] = useState(0);
  const [upcomingBookings, setUpcomingBookings] = useState<any[]>([]);

  // Fetch upcoming bookings for this user/queue.
  // Used to pre-fill entry type and show the booking list below the form.
  useEffect(() => {
    const tokenRaw = localStorage.getItem('Token');
    if (!tokenRaw) return;
    const token = JSON.parse(tokenRaw).token;
    const queueEncoded = encodeURIComponent(props.queueName);
    fetch(`${HTTP_SERVER_URL}/api/queues/${queueEncoded}/my_bookings`, {
      headers: { Authorization: `Token ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then((bookings: any[]) => {
        setUpcomingBookings(bookings);
        // Pre-fill entry type from the active booking (first one in window), but only if not in queue
        if (personalQueueEntry !== null) return;
        const now = new Date();
        const active = bookings.find(b => {
          const slot = new Date(b.slot_time);
          return now >= new Date(slot.getTime() - 30 * 60 * 1000) &&
                 now <= new Date(slot.getTime() + 30 * 60 * 1000);
        });
        if (active) setHelp(active.help);
      })
      .catch(() => {});
  }, [props.queueName]);

  useEffect(() => {
    const newLocation = user?.location || personalQueueEntry?.location || '';
    const newComment = personalQueueEntry?.comment || '';
    const newHelp = personalQueueEntry !== null ? personalQueueEntry.help : true;

    if (user?.location && personalQueueEntry?.location && user?.location !== personalQueueEntry?.location) {
      dispatch(updatePersonalEntry(props.queueName, newComment, newLocation, newHelp));
    }

    setLocation(newLocation);
    setComment(newComment);
    setHelp(newHelp);

  }, [personalQueueEntry, user]);

  function enterQueue(event: any) {
    if (personalQueueEntry) {
      return;
    }

    if (!location || !comment) {
      return;
    }

    if (event.key === 'Enter') {
      dispatch(joinQueue(props.queueName, comment, location, help));
    }
    else if (event.button === 0) {
      if (new Date().getTime() - doubleClickProtection > 750) {
        setDoubleClickProtection(new Date().getTime());
        dispatch(joinQueue(props.queueName, comment, location, help));
      }
    }
  }

  function receiveHelp() {
    if (new Date().getTime() - doubleClickProtection > 750) {
      setDoubleClickProtection(new Date().getTime());
      dispatch(openSelectHelperModal(props.queueName));
    }
  }

  function leave() {
    if (new Date().getTime() - doubleClickProtection > 750) {
      setDoubleClickProtection(new Date().getTime());
      dispatch(leaveQueue(props.queueName))
    }
  }

  function isInWindow(slotTime: string): boolean {
    const now = new Date();
    const slot = new Date(slotTime);
    return now >= new Date(slot.getTime() - 30 * 60 * 1000) &&
           now <= new Date(slot.getTime() + 30 * 60 * 1000);
  }

  function formatSlotTime(slotTime: string): string {
    const slot = new Date(slotTime);
    return slot.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <details className="col-12 p-0" open={!isTA}>
      <summary
        className="clickable"
        style={{ listStyle: 'none', display: 'block', padding: '8px 12px', fontWeight: 600, borderRadius: '4px', background: '#f0f0f0', border: '1px solid #ccc', marginBottom: '4px' }}
      >
        {personalQueueEntry !== null ? 'In queue' : 'Join queue'}
      </summary>
      <div>
        <EnterInformationForm
          {...props}
          location={location}
          setLocation={setLocation}
          comment={comment}
          setComment={setComment}
          help={help}
          setHelp={setHelp} />

        {upcomingBookings.length > 0 && (
          <div className="col-12 mt-2 mb-1">
            {upcomingBookings.map((b: any) => {
              const active = isInWindow(b.slot_time);
              return (
                <div
                  key={b.slot_time}
                  className="text-center py-1 px-2 mb-1 rounded"
                  style={
                    active && !(personalQueueEntry !== null && personalQueueEntry.help !== b.help)
                      ? { background: '#e67e22', color: '#fff' }
                      : { background: '#e0e0e0', color: '#888' }
                  }>
                  <strong>{b.help ? 'Help' : 'Present'}</strong> booked at {formatSlotTime(b.slot_time)}
                  {active && personalQueueEntry !== null && personalQueueEntry.help !== b.help && (
                    <div style={{ fontSize: '0.85em', marginTop: '2px' }}>
                      You are queued for {personalQueueEntry.help ? 'help' : 'presentation'} — no booking priority
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {
          personalQueueEntry !== null
            ? <>
              {
                personalQueueEntry.gettinghelp
                  ? null
                  : <div
                      className="col-12 text-center green white-text clickable"
                      style={{lineHeight: '3em'}}
                      onClick={receiveHelp}>
                      <strong>Receiving help</strong>
                    </div>
              }
                <div
                  className="col-12 text-center red white-text clickable"
                  style={{lineHeight: '3em'}}
                  onClick={leave}>
                  <strong>Leave queue</strong>
                </div>
              </>
            : !isLocked
                ? <div
                    className={`col-12 text-center text-white ${location && comment ? 'theme clickable' : 'gray non-clickable'}`}
                    style={{lineHeight: '3em'}}
                    onClick={enterQueue}>
                    <strong>Join queue</strong>
                  </div>
                : <div
                    className="col-12 text-center gray"
                    style={{lineHeight: '3em'}}
                    onClick={enterQueue}>
                    <strong><Lock /> Queue is locked</strong>
                  </div>
        }
      </div>
    </details>
  );
};
